import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Order } from '@usf/ngrx-order';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { OrderCalculationsDisplayed } from '@shared/models/calculations-displayed-model';
import { PlatformService } from '@panamax/app-state';
import { InsightsProductsFilter } from '@order/pages/order-insights/models/insights-products.model';
import {
  ViewToggleButtons,
  ViewType,
} from '@order/pages/order-insights/models/view-toggle.model';
import { FEATURES } from '@shared/constants/splitio-features';

@Component({
  selector: 'app-order-insights-page-header',
  templateUrl: './order-insights-page-header.component.html',
  styleUrls: ['./order-insights-page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderInsightsPageHeaderComponent {
  @Input() breadcrumbs: Crumb[];
  @Input() order: Order;
  @Input() isLoading = true;
  @Input() quickFilters: InsightsProductsFilter[] = [];
  @Input() resetLabel = false;
  @Input() downloadModalIsOpen$ = new BehaviorSubject<boolean>(false);
  @Input() calculationsDisplayed: OrderCalculationsDisplayed;
  @ViewChild('filterContainer', { static: false }) filterContainer: ElementRef;
  @Output() downloadModalStateChange: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() additionalOptionsClicked = new EventEmitter<MouseEvent>();
  @Output() viewToggled = new EventEmitter<ViewType>();
  @Output() filterSelected = new EventEmitter<{
    filter: InsightsProductsFilter;
    index: number;
  }>();
  readonly pmeViewToggleFeatureFlag = FEATURES.split_dynamic_pme_list.name;
  readonly pmeOrderInsightsFeatureFlag =
    FEATURES.split_dynamic_order_insights_pme.name;
  toggleButtons: ViewToggleButtons = {
    [ViewType.INSIGHTS]: { selected: true },
    [ViewType.PRICING]: { selected: false },
  };
  ViewType = ViewType;

  constructor(readonly platformService: PlatformService) {}

  toggleView(view: ViewType) {
    // Reset all buttons to unselected
    this.toggleButtons[ViewType.INSIGHTS].selected = false;
    this.toggleButtons[ViewType.PRICING].selected = false;
    // Set the clicked button as selected
    this.toggleButtons[view].selected = true;
    this.viewToggled.emit(view);
  }

  openAdditionalOptions(event: MouseEvent) {
    this.additionalOptionsClicked.emit(event);
  }

  showDownloadInsightsModal(isOpen: boolean) {
    this.downloadModalIsOpen$.next(isOpen);
    this.downloadModalStateChange.emit(isOpen);
  }

  toggleStatusFilter(quickFilter, index: number) {
    this.quickFilters.forEach(filter => (filter.selected = false));
    quickFilter.selected = true;
    this.scrollToSingleFilter(index);
    // Emit filter selection to parent page component
    this.filterSelected.emit({ filter: quickFilter, index });
  }

  scrollToSingleFilter(index: number) {
    const containerElement = this.filterContainer?.nativeElement;
    if (!containerElement) return;

    const quickFilters = containerElement.querySelectorAll(
      '.single-filter',
    ) as NodeListOf<HTMLElement>;

    const quickFilter = quickFilters[index];
    if (!quickFilter) return;

    containerElement.scrollTo({
      left: quickFilter.offsetLeft - 50,
      behavior: 'smooth',
    });

    setTimeout(() => {
      if (containerElement) {
        containerElement.classList.remove('add-bounce');
      }
    }, 500);
  }
}
